import {createAsyncThunk, createSlice} from '@reduxjs/toolkit'
import {http} from 'hiya-common/src/utils/axios'
import {useSelector} from 'react-redux'

export interface WeekTaskTierItem {
  target: number
  ack: number
  /** 0 未达 · 1 当前档 · 2 已完成 · 3 */
  status: number
}

export interface WeekTaskSt {
  week_tokens: number
  max_target: number
  total_ack: number
  receive_ack: number
  task_status: number
  task_id?: string
  task_level?: string
  task_param?: number
  items?: WeekTaskTierItem[]
  /** scope=2 时为排除房间口径，禁止前端再减 */
  week_pearls?: number
}

/** 房间政策档位表；单位已 ÷100，原样展示 */
export interface RoomPolicyTableItem {
  level?: number
  require_token?: string | number
  reward_token?: string | number
}

export interface StageSignInState {
  show: boolean
  today_signed: boolean
  week_start: string
  week_remain_ts: number
  consecutive_days: number
  daily_items: {
    day_index: number
    date: string
    status: number
    daily_token: number
    is_today: boolean
  }[]
  streak_items: {
    streak_day: number
    token: number
    ack_claimed: boolean
  }[]
}

export interface StageBandItem {
  stage: string
  min_value: number
  max_value: number
}

export type AckType = 'score' | 'pearl'

export interface StageV2DailyTaskTier {
  target: number
  ack_value: number
  done: boolean
  /** score / pearl；空则走服务端默认 */
  ack_type?: AckType
}

export interface StageV2DailyTaskItem {
  task_id: number
  current_value: number
  tiers: StageV2DailyTaskTier[]
}

export interface PerformanceItem {
  metric_id: number
  target: number
  current: number
  value_granted: boolean
  jump_url: string
  ack_value: number
  target_s: number
  target_ss: number
  ack_type?: AckType
}

export interface StageCenterPopup {
  show_upgrade: boolean
  show_demotion: boolean
  show_retention: boolean
  show_fast_track: boolean
}

/** `/live/hiya_socialize/fetch_level_benefit_usage` */
export interface LevelBenefitUsageFields {
  week_start: string
  match_dispatch_cnt: number
  feed_exposure_pv: number
  deep_chat_dispatch_cnt: number
  user_level: string
  relationship_warming_session_cnt: number
}

export interface RankCenterActiveState {
  avatar: string
  user_stage: string
  need_join: boolean
  week_remain_ts: number
  stage_v2_daily_token_total: number
  stage_v2_week_token_total: number
  stage_v2_week_token_process: number
  stage_sign_in?: StageSignInState
  week_task: WeekTaskSt
  stage_v2_income_value: number
  stage_v2_task_value: number
  stage_v2_perf_value: number
  stage_v2_composite_value: number
  stage_bands: StageBandItem[]
  stage_effective_start: string
  stage_effective_end: string
  fast_track_active: boolean
  stage_v2_daily_tasks: StageV2DailyTaskItem[]
  performance_items: PerformanceItem[]
  stage_center_popup: StageCenterPopup
  show_low_health_bar: boolean
  benefits_url: string
  /** 1 社交政策全部结算 / 2 社交政策排除语音房；缺省按 1 */
  income_scope?: number
  /** 当天语音房收礼（÷100，与 week_tokens 同单位） */
  today_room_gift_token?: number
  /** 当天排除语音房后的结算收入（÷100） */
  today_non_room_token?: number
  /** 当周语音房收礼（÷100） */
  week_room_gift_token?: number
  /** 当周排除语音房后的结算收入（÷100） */
  week_non_room_token?: number
  room_policy_table?: RoomPolicyTableItem[]
  /** 当周语音房收礼 / 总珍珠 > 0.4；仅配置语音房政策（有 room_task）时有意义 */
  prefer_room_tab?: boolean
  /** 语音房结算周任务，与 week_task 同形；配置语音房政策时下发，否则不下发 / null */
  room_task?: WeekTaskSt | null
}

interface RankCenterState {
  active: RankCenterActiveState
  rights: LevelBenefitUsageFields
}

const initialState: RankCenterState = {
  active: {
    avatar: '',
    user_stage: 'N',
    need_join: false,
    week_remain_ts: 0,
    stage_v2_daily_token_total: 0,
    stage_v2_week_token_total: 0,
    stage_v2_week_token_process: 0,
    week_task: {
      week_tokens: 0,
      max_target: 0,
      total_ack: 0,
      receive_ack: 0,
      task_status: 0,
    },
    stage_v2_income_value: 0,
    stage_v2_task_value: 0,
    stage_v2_perf_value: 0,
    stage_v2_composite_value: 0,
    stage_bands: [],
    stage_effective_start: '',
    stage_effective_end: '',
    fast_track_active: false,
    stage_v2_daily_tasks: [],
    performance_items: [],
    stage_center_popup: {
      show_upgrade: false,
      show_demotion: false,
      show_retention: false,
      show_fast_track: false,
    },
    show_low_health_bar: false,
    benefits_url: '',
  },
  rights: {
    week_start: '',
    match_dispatch_cnt: 0,
    feed_exposure_pv: 0,
    deep_chat_dispatch_cnt: 0,
    user_level: 'N',
    relationship_warming_session_cnt: 0,
  },
}

export const fetch_female_level_center_page = createAsyncThunk(
  'RankCenter/fetch_female_level_center_page',
  async (params: Record<string, never>, store: any) => {
    const resp = await http.post(
      '/live/saada/fetch_center_page',
      {
        ...params,
      },
      {store, xcErrorToast: true}
    )
    return (resp?.data?.data || {}) as Partial<RankCenterActiveState>
  }
)

export const fetch_level_benefit_usage = createAsyncThunk(
  'RankCenter/fetch_level_benefit_usage',
  async (params: Record<string, never>, store: any) => {
    const resp = await http.post(
      '/live/hiya_socialize/fetch_level_benefit_usage',
      {...params},
      {store, xcErrorToast: true}
    )
    return (resp?.data?.data || {}) as Partial<RankCenterActiveState>
  }
)

export type AcquireFemaleTaskRewardParams = {
  task_id?: string
  task_type: 'novice_task' | 'daily_task' | 'weekly_task'
  task_level?: string
  task_param?: number
}

export const acquire_female_task_reward = createAsyncThunk(
  'RankCenter/acquire_female_task_reward',
  async (params: AcquireFemaleTaskRewardParams, store: any) => {
    const resp = await http.post(
      '/live/saada/acquire_f_task_rd',
      {
        ...params,
      },
      {store, xcErrorToast: true}
    )
    return resp?.data?.data || {}
  }
)

export type AcquireRoomTaskRewardResult = {
  err_msg?: string
  list?: {
    level: number
    cfg_require_token: number
    cfg_reward_token: number
  }[]
}

/** Wiki §5：领取当前周期全部待领语音房档；不要走 acquire_f_task_rd */
export const acquire_room_task_reward = createAsyncThunk(
  'RankCenter/acquire_room_task_reward',
  async (_params: Record<string, never>, store: any) => {
    const resp = await http.post(
      '/live/saada/acquire_room_task_reward',
      {},
      {store, xcErrorToast: true}
    )
    return (resp?.data?.data || {}) as AcquireRoomTaskRewardResult
  }
)

export const fe_sign_in = createAsyncThunk(
  'RankCenter/female_level_sign_in',
  async (params: Record<string, never>, store: any) => {
    const resp = await http.post(
      '/live/hiya_socialize/sign_in',
      {...params},
      {store, xcErrorToast: true}
    )
    return resp?.data?.data || {}
  }
)

export const RankCenterSlice = createSlice({
  name: 'RankCenter',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(
      fetch_female_level_center_page.fulfilled,
      (state, action) => {
        Object.assign(state.active, action.payload || {})
      }
    )
    builder.addCase(fetch_level_benefit_usage.fulfilled, (state, action) => {
      Object.assign(state.rights, action.payload || {})
    })
  },
})

export default RankCenterSlice.reducer
export const RankCenterActions = RankCenterSlice.actions
export const useRankCenterState = () =>
  useSelector((store: any) => store.RankCenter) as RankCenterState
